import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { X, Check } from 'lucide-react';
import { collection, addDoc, onSnapshot, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { cn } from '../lib/utils';
import { celebrate } from '../lib/haptics';
import { track, markCheckoutStarted } from '../lib/analytics';
import { Capacitor } from '@capacitor/core';
import {
  getElitePackages,
  getTrialTerms,
  purchaseElite,
  restorePurchases,
  type ElitePackages,
  type TrialTerms,
} from '../lib/iap';
import type { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { TermsOfService, PrivacyPolicy } from './LegalModals';

const IS_NATIVE = Capacitor.isNativePlatform();

const PRICES = {
  annual: {
    id: 'price_1TX0q81LYtrywT8iwjOMFB5n',
    displayPrice: '$4.99',
    per: '/mo',
    subtext: 'billed $59.99/year',
  },
  monthly: {
    id: 'price_1TUrQc1LYtrywT8i60N44Ezi',
    displayPrice: '$9.99',
    per: '/mo',
    subtext: null,
  },
};

// Mirrors the approved landing-page Elite pillars: "develop every player"
// (xOVR, Smart Scout AI, opponent intel, pitching) then "run the whole
// program" (multi-team, assistants, tournaments). Lead with the AI headliners.
const FEATURES = [
  'xOVR: your stats meet your own scouting',
  'Smart Scout AI insights for every player',
  'Post-game insights from your game stats',
  'Opponent scouting & intel before you face them',
  'Pitching Intel: who to bring in, and when',
  'Lineup Builder smart enough for the moment',
  'All your teams in one app, switch in a tap',
  'Assistant-coach collaboration on player skills',
  'Tournament mode for those big weekends',
];

interface Props {
  uid: string;
  mode: 'onboarding' | 'upgrade';
  isElite?: boolean;
  onDismiss: () => void;
}

export function EliteUpgradeModal({ uid, mode, isElite = false, onDismiss }: Props) {
  // Annual is the hero plan — it's the default selection so the coach lands on
  // the best-value option (matches the "annual is the hero" pricing guidance).
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Apple Guideline 3.1.2 — paywall must surface the auto-renewal terms plus
  // links to the EULA / Privacy Policy. These toggle the full legal modals.
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // ── Native IAP: fetch RevenueCat packages so we show App Store prices ──
  const [iapPackages, setIapPackages] = useState<ElitePackages>({ monthly: null, annual: null });
  // If the offering can't be fetched (offline, App Store hiccup), we must not
  // sit on "Loading pricing…" with a dead CTA forever — surface it + allow retry.
  const [iapLoadFailed, setIapLoadFailed] = useState(false);
  const [iapFetchAttempt, setIapFetchAttempt] = useState(0);
  useEffect(() => {
    if (!IS_NATIVE) return;
    let cancelled = false;
    setIapLoadFailed(false);
    getElitePackages().then((pkgs) => {
      if (cancelled) return;
      setIapPackages(pkgs);
      setIapLoadFailed(!pkgs.monthly && !pkgs.annual);
    });
    return () => { cancelled = true; };
  }, [iapFetchAttempt]);

  // ── Trial terms (Apple 3.1.2c) ──
  // The trial length + post-trial charge must come from the store, per selected
  // plan, and must reflect THIS Apple ID's intro-offer eligibility. Null terms
  // mean we don't yet know — the paywall stays silent about a trial until we do.
  const [trialTerms, setTrialTerms] = useState<TrialTerms | null>(null);
  useEffect(() => {
    if (!IS_NATIVE) return;
    const pkg = iapPackages[selectedPlan];
    if (!pkg) { setTrialTerms(null); return; }
    let cancelled = false;
    setTrialTerms(null);
    getTrialTerms(pkg).then((terms) => {
      if (!cancelled) setTrialTerms(terms);
    });
    return () => { cancelled = true; };
  }, [iapPackages, selectedPlan]);

  async function handleNoThanks() {
    await setDoc(doc(db, 'users', uid), { seenTrialOffer: true }, { merge: true });
    onDismiss();
  }

  // ── Native purchase via RevenueCat / StoreKit ──
  async function startNativePurchase() {
    if (isElite) {
      setError('You already have an active Elite subscription.');
      return;
    }
    const pkg: PurchasesPackage | null = iapPackages[selectedPlan];
    if (!pkg) {
      setError('This plan is not available right now. Please try again.');
      return;
    }
    setLoading(true);
    setError(null);
    // The native equivalent of checkout_opened. Apple's sheet is presented by
    // purchaseElite, so this is the last moment before the coach is asked to pay.
    track('checkout_opened', { plan: selectedPlan, platform: 'ios' });
    const result = await purchaseElite(pkg);
    setLoading(false);
    if (result.isElite) {
      // IAP completes in-app, so there is no redirect to resolve on return the
      // way Stripe needs: fire the conversion directly.
      track('checkout_completed', { plan: selectedPlan, platform: 'ios' });
      celebrate(); // Elite unlocked — the marquee success moment
      onDismiss(); // entitlement flows through useSubscription's live listener
    } else if (result.error) {
      setError(result.error);
    }
    // result.ok === false with no error => user cancelled the sheet; stay open.
  }

  async function handleRestore() {
    setRestoring(true);
    setError(null);
    const { isElite: restored, error: restoreErr } = await restorePurchases();
    setRestoring(false);
    if (restored) {
      celebrate(); // Elite restored
      onDismiss();
    } else {
      setError(restoreErr ?? 'No previous Elite purchase found to restore.');
    }
  }

  async function startCheckout() {
    if (isElite) {
      setError('You already have an active Elite subscription.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const sessionRef = await addDoc(
        collection(db, 'customers', uid, 'checkout_sessions'),
        {
          price: PRICES[selectedPlan].id,
          trial_period_days: 14,
          allow_promotion_codes: true,
          success_url: window.location.origin,
          cancel_url: window.location.origin,
        }
      );
      onSnapshot(sessionRef, (snap) => {
        const data = snap.data();
        if (data?.error) {
          setError(data.error.message ?? 'Something went wrong. Please try again.');
          setLoading(false);
        }
        if (data?.url) {
          // Last event before the app hands off to Stripe. The gap between this
          // and checkout_completed is the credit-card wall, and that number
          // decides whether the trial stays card-required.
          track('checkout_opened', { plan: selectedPlan });
          markCheckoutStarted(selectedPlan);
          window.location.assign(data.url);
        }
      });
    } catch {
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  const isOnboarding = mode === 'onboarding';
  const price = PRICES[selectedPlan];

  // On native, the displayed price MUST come from the App Store (Apple charges
  // the store price as one transaction — the web "$4.99/mo billed yearly"
  // reframe can't be replicated natively or it's a price-mismatch rejection).
  const nativePkg = IS_NATIVE ? iapPackages[selectedPlan] : null;
  // Until the RevenueCat offering loads, we have no real price yet. We must NOT
  // fall back to the web price on native (that's the $4.99 bug) — show a loader.
  const priceLoading = IS_NATIVE && !nativePkg;

  // Native: the real store price string (e.g. "$59.99"), already includes the
  // currency symbol and the full charge amount. Web: the marketing per-month.
  const displayPrice = IS_NATIVE ? (nativePkg?.product.priceString ?? '') : price.displayPrice;
  // "/mo" suffix is only meaningful for the web per-month figure.
  const showPer = !IS_NATIVE;

  // For the annual plan on native, derive an honest "≈$X/mo" helper from the
  // real yearly price so the per-month value framing survives without lying
  // about what Apple charges. priceString is "$59.99" → numeric 59.99 → /12.
  const nativePerMonth = (() => {
    if (!IS_NATIVE || selectedPlan !== 'annual' || !nativePkg) return null;
    const raw = nativePkg.product.price; // numeric major-units price from RevenueCat
    if (typeof raw !== 'number' || raw <= 0) return null;
    const symbol = nativePkg.product.priceString.replace(/[\d.,\s]/g, '') || '$';
    return `${symbol}${(raw / 12).toFixed(2)}`;
  })();

  const subtext = IS_NATIVE
    ? (selectedPlan === 'annual'
        ? (nativePerMonth ? `Billed yearly · about ${nativePerMonth}/mo` : 'Billed yearly')
        : 'Billed monthly')
    : price.subtext;

  // ── Apple 3.1.2(c): trial terms must be unambiguous in the binding view ──
  // Web keeps the Stripe 14-day trial (trial_period_days below). On native we
  // only claim a trial when the store confirms one AND this Apple ID is still
  // eligible; otherwise the paywall sells at full price with no trial language.
  const hasTrial = IS_NATIVE ? !!trialTerms?.duration : true;
  // Native purchases can't proceed until the offering AND the terms have loaded
  // — buying before we know the terms means buying on an unverified promise.
  const termsLoading = IS_NATIVE && !trialTerms;
  const ctaDisabled = loading || restoring || priceLoading || termsLoading;

  // The one line Apple is looking for: how long the trial runs, and exactly what
  // gets charged when it ends. e.g. "14 days free, then $59.99/year."
  const termsLine = IS_NATIVE
    ? (trialTerms
        ? (trialTerms.duration
            ? `${trialTerms.duration} free, then ${trialTerms.renewalPrice}. Renews automatically until canceled.`
            : `${trialTerms.renewalPrice}. Renews automatically until canceled.`)
        : null)
    : '14 days free, then billed automatically. Cancel anytime.';

  const ctaLabel = loading
    ? 'Starting...'
    : hasTrial ? 'Start Free Trial →' : 'Subscribe →';

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-[rgba(0,0,0,0.45)] backdrop-blur-sm" onClick={isOnboarding ? undefined : onDismiss} />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 300 }}
        className="relative bg-canvas border border-hairline rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 pb-8 shadow-2xl shadow-emerald-500/10"
      >
        {!isOnboarding && (
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 text-ink-2 hover:text-ink transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-2xl overflow-hidden shrink-0 shadow-sm">
            <img src="/dugout-mark.png" alt="Dugout" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="font-black text-ink text-2xl leading-tight">Dugout Elite</h2>
            <p className="text-ink-2 text-xs">Auto-renewable subscription · {selectedPlan === 'annual' ? 'billed yearly' : 'billed monthly'}</p>
          </div>
        </div>

        {/* Pill switcher */}
        <div className="flex gap-2 mt-5 mb-5 bg-raised p-1 rounded-2xl">
          <button
            onClick={() => setSelectedPlan('monthly')}
            className={cn(
              "flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
              selectedPlan === 'monthly'
                ? "bg-ink text-canvas shadow-md"
                : "text-ink-2 hover:text-ink-2"
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedPlan('annual')}
            className={cn(
              "flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5",
              selectedPlan === 'annual'
                ? "bg-ink text-canvas shadow-md"
                : "text-ink-2 hover:text-ink-2"
            )}
          >
            Annual
            <span className={cn(
              "text-[9px] font-black px-1.5 py-0.5 rounded-full",
              selectedPlan === 'annual' ? "bg-ink/20 text-canvas" : "bg-canvas text-accent-ink"
            )}>
              Save 50%
            </span>
          </button>
        </div>

        {/* Price display */}
        <div className="mb-5">
          {priceLoading ? (
            // Native, offering still loading — never show a stale web price.
            iapLoadFailed ? (
              <div className="flex flex-col items-start gap-2 h-[60px] justify-center">
                <span className="text-ink-2 text-sm">Couldn’t load pricing from the App Store.</span>
                <button
                  onClick={() => setIapFetchAttempt(a => a + 1)}
                  className="text-xs font-semibold uppercase tracking-widest text-accent-ink"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 h-[60px]">
                <div className="w-5 h-5 border-2 border-ink-mute border-t-ink-2 rounded-full animate-spin" />
                <span className="text-ink-2 text-sm">Loading pricing…</span>
              </div>
            )
          ) : (
            <>
              <div className="flex items-end gap-1">
                <span className="text-5xl font-black text-ink">{displayPrice}</span>
                {showPer && <span className="text-ink-2 text-sm mb-2">{price.per}</span>}
              </div>
              {subtext && (
                <p className="text-ink-2 text-xs mt-0.5">{subtext}</p>
              )}
            </>
          )}
        </div>

        {/* Feature list */}
        <ul className="space-y-2.5 mb-6">
          {FEATURES.map(feature => (
            <li key={feature} className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-canvas flex items-center justify-center shrink-0">
                <Check className="w-3 h-3 text-accent-ink" />
              </div>
              <span className="text-sm text-ink-2 font-medium">{feature}</span>
            </li>
          ))}
        </ul>

        {error && (
          <p className="text-danger text-xs text-center mb-3">{error}</p>
        )}

        {/* CTA */}
        <button
          onClick={IS_NATIVE ? startNativePurchase : startCheckout}
          disabled={ctaDisabled}
          className="w-full py-4 bg-ink hover:bg-ink disabled:opacity-60 disabled:cursor-not-allowed text-canvas rounded-2xl font-black text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-ink/30 border-t-white rounded-full animate-spin" />
          ) : null}
          {ctaLabel}
        </button>

        {/* Apple 3.1.2(c): the trial length and the exact post-trial charge must
            sit with the CTA. The old "No charge today" sold the free part while
            staying silent on the automatic renewal — that's what got rejected. */}
        {termsLine && (
          <p className="text-ink-2 text-[11px] text-center mt-3 px-2 leading-relaxed">
            {termsLine}
          </p>
        )}

        {/* Billing mechanics + EULA / Privacy links — Apple Guideline 3.1.2
            requires both in the binding paywall view. The trial length and
            renewal price live in termsLine above; this covers how to cancel so
            neither fact is stated twice. Links show on web + native. */}
        <p className="text-ink-2 text-[10px] leading-relaxed text-center mt-3 px-2">
          {IS_NATIVE && (
            <>
              Payment is charged to your Apple ID at confirmation of purchase.
              Cancel at least 24 hours before the end of the current period to
              avoid being charged; manage or cancel anytime in your App Store
              account settings.{' '}
            </>
          )}
          By continuing you agree to our{' '}
          <button
            onClick={() => setShowTerms(true)}
            className="underline text-ink-2 hover:text-ink-2"
          >
            Terms of Use
          </button>{' '}
          and{' '}
          <button
            onClick={() => setShowPrivacy(true)}
            className="underline text-ink-2 hover:text-ink-2"
          >
            Privacy Policy
          </button>
          .
        </p>

        {/* Restore Purchases — Apple requires this for subscription apps. */}
        {IS_NATIVE && (
          <div className="text-center mt-3">
            <button
              onClick={handleRestore}
              disabled={loading || restoring}
              className="text-ink-2 hover:text-ink-2 text-xs transition-colors disabled:opacity-40"
            >
              {restoring ? 'Restoring…' : 'Restore Purchases'}
            </button>
          </div>
        )}

        {isOnboarding && (
          <div className="text-center mt-3">
            <button
              onClick={handleNoThanks}
              disabled={loading || restoring}
              className="text-ink-2 hover:text-ink-2 text-sm transition-colors disabled:opacity-40"
            >
              No thanks, start with Free features
            </button>
          </div>
        )}
      </motion.div>

      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </div>
  );
}
